import Notiflix from 'notiflix'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthUserData } from '../../../../authUserProvider'
import { InputFieldAddressLookup } from '../../../../components/reusable/inputFieldAddressLookup'
import { InputFieldGoogleAddressLookup } from '../../../../components/reusable/inputFieldGoogleAddressLookup'
import { DialCodeSelect } from '../../../../components/reusable/dialCodeSelect'
import { GooglePlaceAddress } from '../../../../services/google-places'
import { dialCodes } from '../../../../data/dialCodes'
import { Data8Service } from '../../../../services/data-8'
import {
    SERVICE_BRANCHES,
    TITLES,
    useMilitaryHearingLossForm,
} from '../hooks/useMilitaryHearingLossForm/useMilitaryHearingLossForm'
import { EMAIL_REGEX, NI_REGEX, TEL_REGEX, SERVICE_NUMBER_REGEX, POSTCODE_REGEX, INTERNATIONAL_POSTCODE_MAX, mobileMinLength, validateForm } from '../hooks/useMilitaryHearingLossForm/militaryHearingLossFormUtils'
import { ToggleInput } from '../components/ToggleInput/ToggleInput'
import { SegmentedToggle } from '../components/SegmentedToggle/SegmentedToggle'
import { FormProgressPanel } from '../FormProgressPanel/FormProgressPanel'
import { DateInput } from '../components/DateInput/DateInput'
import { PayloadDebugPanel } from '../components/PayloadDebugPanel/PayloadDebugPanel'
import { AudiologyFileInput } from '../components/AudiologyFileInput/AudiologyFileInput'
import { IdentityDocumentsInput } from '../components/IdentityDocumentsInput/IdentityDocumentsInput'
import { AUDIOLOGY_OPTIONS } from '../shared/mhlFormatting'
import styles from './MilitaryHearingLossForm.module.scss'
import { colors } from '../../../../ui'

// Maps the comma-joined string from Data8 into address lines 1–4 + postcode.
const parseAddress = (joined: string): { addressLine1: string; addressLine2: string; addressLine3: string; addressLine4: string; postcode: string } => {
    const parts = joined.split(',').map((p) => p.trim()).filter(Boolean)
    if (parts.length === 0) return { addressLine1: '', addressLine2: '', addressLine3: '', addressLine4: '', postcode: '' }

    const postcode = parts[parts.length - 1] || ''
    const remaining = parts.slice(0, -1)

    const [addressLine1 = '', addressLine2 = '', addressLine3 = '', addressLine4 = ''] = remaining
    return { addressLine1, addressLine2, addressLine3, addressLine4, postcode }
}

type AddressRegion = 'uk' | 'international'

const ADDRESS_REGION_OPTIONS: { value: AddressRegion; label: string }[] = [
    { value: 'uk', label: 'In the UK' },
    { value: 'international', label: 'Outside the UK' },
]

const countryOptions = [...dialCodes].sort((a, b) => a.name.localeCompare(b.name))

const toTitleCase = (v: string) =>
    v.replace(/\b\w/g, (c) => c.toUpperCase())

const SectionCheck = () => (
    <span className={styles.sectionCheck} aria-label="Section complete">
        <svg width="11" height="9" viewBox="0 0 11 9" fill="none" aria-hidden="true">
            <path d="M1 4.5L4 7.5L10 1.5" stroke={colors.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    </span>
)

export const MilitaryHearingLossForm = () => {
    const authUser = useAuthUserData()
    const navigate = useNavigate()
    const agentLabel = authUser?.name || authUser?.email || 'unknown'

    const [selectedAddressRaw, setSelectedAddressRaw] = useState('')
    const [manualAddress, setManualAddress] = useState(false)
    // UK uses the Data8 lookup; outside the UK uses Google Places. Agents mostly enter UK clients.
    const [addressRegion, setAddressRegion] = useState<AddressRegion>('uk')
    // Set when a Google pick is a street with no house/building number
    const [buildingNumberMissing, setBuildingNumberMissing] = useState(false)
    const [addressKey, setAddressKey] = useState(0)
    const [emailError, setEmailError] = useState<string | null>(null)
    const [mobileError, setMobileError] = useState<string | null>(null)
    const [homePhoneError, setHomePhoneError] = useState<string | null>(null)
    const [niError, setNiError] = useState<string | null>(null)
    const [postcodeError, setPostcodeError] = useState<string | null>(null)
    const [dobError, setDobError] = useState<string | null>(null)
    // Per-service date errors keyed by `${service.id}-enlistment` / `${service.id}-discharge`.
    // Using the stable service id (not the array index) keeps errors aligned when a
    // middle service is removed.
    // DateInput emits '' for both empty and invalid values, so the progress panel
    // can't tell them apart without this explicit error signal.
    const [serviceDateErrors, setServiceDateErrors] = useState<Record<string, boolean>>({})
    const [emailData8Valid, setEmailData8Valid] = useState(true)
    const [mobileData8Valid, setMobileData8Valid] = useState(true)
    const emailDebounce = useRef<ReturnType<typeof setTimeout> | null>(null)
    const mobileDebounce = useRef<ReturnType<typeof setTimeout> | null>(null)

    const {
        form,
        setField,
        setServiceField,
        addService,
        removeService,
        audiogramFile,
        setAudiogramFile,
        audiogramUpload,
        retryAudiogramUpload,
        poidFile,
        setPoidFile,
        utilityBillFile,
        setUtilityBillFile,
        identityUpload,
        retryIdentityUpload,
        submit,
        reset,
        isValid,
        isSubmitting,
        submitError,
        submitted,
        clientId,
        submittedContact,
    } = useMilitaryHearingLossForm(agentLabel)

    const t = (v: string) => v.trim() !== ''

    const sectionComplete = {
        personal:
            t(form.title) && form.firstname.trim().length >= 2 && form.lastname.trim().length >= 2 && t(form.dob) &&
            t(form.nationalInsurance) && NI_REGEX.test(form.nationalInsurance.replace(/\s/g, '')) && !niError &&
            poidFile !== null && utilityBillFile !== null,
        address:
            t(form.address1) && t(form.address3) && t(form.country) && !postcodeError && !buildingNumberMissing &&
            (form.country === 'GB'
                ? t(form.postcode) && POSTCODE_REGEX.test(form.postcode.trim())
                : form.postcode.trim().length <= INTERNATIONAL_POSTCODE_MAX),
        contact:
            t(form.telephone) && TEL_REGEX.test(form.telephone.trim()) &&
            form.telephone.trim().length >= mobileMinLength(form.telephoneCountry) && form.telephone.trim().length <= 20 && mobileData8Valid && !mobileError &&
            t(form.email) && EMAIL_REGEX.test(form.email.trim()) && emailData8Valid && !emailError,
        services:
            form.services.every((s) =>
                t(s.serviceNumber) && SERVICE_NUMBER_REGEX.test(s.serviceNumber.trim()) &&
                s.branch !== '' && t(s.enlistmentDate) && (s.activeService || t(s.dischargeDate))
            ),
        medical:
            form.audiology !== '' && audiogramFile !== null && form.hearingLossOrTinnitus !== null && form.hyperacusis !== null &&
            form.alternativeNoiseExposure !== '' && form.alternateCivilianExposure !== null,
        other:
            form.successfulAfcsWarPensionClaim !== null &&
            (form.successfulAfcsWarPensionClaim !== true || (form.afcsAwardAmount !== null && form.afcsAwardAmount > 0)) &&
            form.nfciClaim !== null && form.specialForces !== null,
        confirmation: form.matrixOptInConfirmed,
    }

    // True when the Matrix opt-in checkbox is the only thing holding Submit back.
    // Used to explain the greyed-out button rather than leaving the agent guessing.
    const onlyConfirmationOutstanding =
        !form.matrixOptInConfirmed &&
        audiogramFile !== null &&
        poidFile !== null &&
        utilityBillFile !== null &&
        emailData8Valid &&
        mobileData8Valid &&
        validateForm({ ...form, matrixOptInConfirmed: true }) === null

    const handleReusableSave = (obj: { field: string; value: string }) => {
        if (obj.field === 'address') {
            setSelectedAddressRaw(obj.value)
            const parsed = parseAddress(obj.value)
            setField('address1', parsed.addressLine1)
            setField('address2', parsed.addressLine2)
            setField('address3', parsed.addressLine3)
            setField('address4', parsed.addressLine4)
            setField('postcode', parsed.postcode)
            setField('country', 'GB')
            setPostcodeError(parsed.postcode && !POSTCODE_REGEX.test(parsed.postcode.trim()) ? 'Invalid UK postcode' : null)
        }
        return { success: true, error: '' }
    }

    const selectAddressRegion = (region: AddressRegion) => {
        if (region === addressRegion) return
        setAddressRegion(region)
        // Switching between UK and international lookups starts the address from scratch
        setField('address1', '')
        setField('address2', '')
        setField('address3', '')
        setField('address4', '')
        setField('postcode', '')
        setField('country', region === 'uk' ? 'GB' : '')
        setSelectedAddressRaw('')
        setManualAddress(false)
        setAddressKey((k) => k + 1)
        setPostcodeError(null)
        setBuildingNumberMissing(false)
    }

    const selectGoogleAddress = (address: GooglePlaceAddress, label: string) => {
        setSelectedAddressRaw(label)
        setField('address1', address.address1)
        setField('address2', address.address2)
        setField('address3', address.city)
        setField('address4', '')
        setField('postcode', address.postcode.toUpperCase())
        setField('country', address.country)
        setPostcodeError(
            address.country === 'GB' && !POSTCODE_REGEX.test(address.postcode.trim()) ? 'Invalid UK postcode' : null
        )
        setBuildingNumberMissing(!address.hasBuildingNumber)
        if (!address.hasBuildingNumber) {
            // Fields render after this pick, so wait a tick before focusing
            setTimeout(() => document.getElementById('mhl-field-address1')?.focus(), 0)
        }
    }

    const handleAddress1Change = (value: string) => {
        setField('address1', value)
        // Any edit counts as adding the number, so named houses without one aren't blocked
        if (buildingNumberMissing) setBuildingNumberMissing(false)
    }

    const handleCountryChange = (country: string) => {
        setField('country', country)
        handlePostcodeChange(form.postcode, country)
    }

    const handleMobileCountryChange = (country: string) => {
        setField('telephoneCountry', country)
        if (form.telephone.trim()) handleMobileChange(form.telephone, country)
    }

    const handleEmailChange = (value: string) => {
        setField('email', value)
        setEmailError(null)
        setEmailData8Valid(true)
        if (emailDebounce.current) clearTimeout(emailDebounce.current)
        emailDebounce.current = setTimeout(async () => {
            if (!value.trim()) return
            if (!EMAIL_REGEX.test(value.trim())) {
                setEmailError('Please enter a valid email address')
                setEmailData8Valid(false)
                return
            }
            try {
                const result = await Data8Service('EmailValidation', 'IsValid', { email: value.trim() })
                if (result?.Result === 'Invalid') {
                    setEmailError('Email address appears to be invalid')
                    setEmailData8Valid(false)
                } else {
                    setEmailError(null)
                    setEmailData8Valid(true)
                }
            } catch {
                // Data8 unavailable — allow submission to proceed
                setEmailData8Valid(true)
            }
        }, 400)
    }

    const handleMobileChange = (value: string, country: string = form.telephoneCountry) => {
        setField('telephone', value)
        setMobileError(null)
        setMobileData8Valid(true)
        if (mobileDebounce.current) clearTimeout(mobileDebounce.current)
        mobileDebounce.current = setTimeout(async () => {
            if (!value.trim()) return
            if (!TEL_REGEX.test(value.trim())) {
                setMobileError('Mobile number contains invalid characters')
                setMobileData8Valid(false)
                return
            }
            if (value.trim().length < mobileMinLength(country)) {
                setMobileError('Mobile number is too short')
                setMobileData8Valid(false)
                return
            }
            if (value.trim().length > 20) {
                setMobileError('Mobile number is too long')
                setMobileData8Valid(false)
                return
            }
            try {
                const result = await Data8Service('PhoneValidation', 'IsValid', {
                    telephoneNumber: value.trim(),
                    defaultCountry: country,
                })
                if (result?.Result?.ValidationResult === 'Invalid') {
                    setMobileError('Mobile number appears to be invalid')
                    setMobileData8Valid(false)
                } else {
                    setMobileError(null)
                    setMobileData8Valid(true)
                }
            } catch {
                // Data8 unavailable — allow submission to proceed
                setMobileData8Valid(true)
            }
        }, 400)
    }

    const handleHomePhoneChange = (value: string) => {
        setField('homeTelephone', value)
        if (!value.trim()) { setHomePhoneError(null); return }
        if (!TEL_REGEX.test(value.trim())) {
            setHomePhoneError('Home telephone contains invalid characters')
        } else if (value.trim().length < 9) {
            setHomePhoneError('Home telephone is too short')
        } else if (value.trim().length > 20) {
            setHomePhoneError('Home telephone is too long')
        } else {
            setHomePhoneError(null)
        }
    }

    const handleNiChange = (value: string) => {
        const stripped = value.toUpperCase().replace(/\s/g, '')
        setField('nationalInsurance', stripped)
        if (!stripped) { setNiError(null); return }
        if (!NI_REGEX.test(stripped)) {
            setNiError('Invalid NI number — expected format: AB123456C')
        } else {
            setNiError(null)
        }
    }

    const handlePostcodeChange = (value: string, country: string = form.country) => {
        const upper = value.toUpperCase()
        setField('postcode', upper)
        if (!upper.trim()) { setPostcodeError(null); return }
        if (country !== 'GB') {
            setPostcodeError(
                upper.trim().length > INTERNATIONAL_POSTCODE_MAX ? `Max ${INTERNATIONAL_POSTCODE_MAX} characters` : null
            )
            return
        }
        if (!POSTCODE_REGEX.test(upper.trim())) {
            setPostcodeError('Invalid UK postcode')
        } else {
            setPostcodeError(null)
        }
    }

    const resetAll = () => {
        reset()
        setSelectedAddressRaw('')
        setManualAddress(false)
        setAddressRegion('uk')
        setBuildingNumberMissing(false)
        setAddressKey((k) => k + 1)
        setNiError(null)
        setPostcodeError(null)
        setEmailError(null)
        setMobileError(null)
        setHomePhoneError(null)
        setDobError(null)
        setServiceDateErrors({})
        setEmailData8Valid(true)
        setMobileData8Valid(true)
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        const result = await submit()
        if (result.success) {
            Notiflix.Notify.success('Client submitted for review')
        }
    }

    if (submitted) {
        const clientName = [submittedContact?.firstname, submittedContact?.lastname].filter(Boolean).join(' ')
        const uploading = audiogramUpload.status === 'uploading' || identityUpload.status === 'uploading'
        const uploadFailed = audiogramUpload.status === 'error' || identityUpload.status === 'error'
        const uploadDone = audiogramUpload.status === 'done' && identityUpload.status === 'done'
        return (
            <div className={styles.successPanel}>
                <div className={`${styles.successIcon} ${uploadFailed ? styles.successIconWarn : ''}`}>
                    {uploading ? (
                        <span className={styles.spinner} />
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    )}
                </div>
                <h3>Submitted for review{clientName ? ` — ${clientName}` : ''}</h3>
                <p>
                    The client record has been created and is now <strong>awaiting review</strong>. Once the head of
                    Military Hearing Loss approves the file, the CFA e-sign link will be released to the client.
                </p>

                <div className={`${styles.uploadStatus} ${audiogramUpload.status === 'error' ? styles.uploadStatusError : audiogramUpload.status === 'done' ? styles.uploadStatusOk : ''}`}>
                    {audiogramUpload.status === 'uploading' && <span>Uploading audiogram{audiogramFile ? ` · ${audiogramFile.name}` : ''}…</span>}
                    {audiogramUpload.status === 'done' && (
                        <>
                            <span>Audiogram attached</span>
                            <span className={styles.uploadFileName}>{audiogramUpload.fileName}</span>
                        </>
                    )}
                    {audiogramUpload.status === 'error' && (
                        <>
                            <span>Audiogram upload failed: {audiogramUpload.message}</span>
                            <div className={styles.uploadRetryRow}>
                                <button
                                    type="button"
                                    className={styles.secondaryButton}
                                    onClick={() => {
                                        void retryAudiogramUpload()
                                    }}
                                >
                                    Retry upload
                                </button>
                                <label className={styles.secondaryButton}>
                                    Choose a different file
                                    <input
                                        type="file"
                                        accept=".pdf,.png,.jpg,.jpeg,.heic,.heif"
                                        style={{ display: 'none' }}
                                        onChange={(e) => {
                                            const f = e.target.files?.[0]
                                            if (f) void retryAudiogramUpload(f)
                                            e.target.value = ''
                                        }}
                                    />
                                </label>
                            </div>
                        </>
                    )}
                    {audiogramUpload.status === 'idle' && <span>No audiogram attached.</span>}
                </div>

                <div className={`${styles.uploadStatus} ${identityUpload.status === 'error' ? styles.uploadStatusError : identityUpload.status === 'done' ? styles.uploadStatusOk : ''}`}>
                    {identityUpload.status === 'uploading' && <span>Uploading identity documents…</span>}
                    {identityUpload.status === 'done' && (
                        <>
                            <span>Identity documents attached</span>
                            {poidFile && <span className={styles.uploadFileName}>Proof of ID · {poidFile.name}</span>}
                            {utilityBillFile && <span className={styles.uploadFileName}>Utility bill · {utilityBillFile.name}</span>}
                        </>
                    )}
                    {identityUpload.status === 'error' && (
                        <>
                            <span>Identity upload failed: {identityUpload.message}</span>
                            <div className={styles.uploadRetryRow}>
                                <button
                                    type="button"
                                    className={styles.secondaryButton}
                                    onClick={() => {
                                        void retryIdentityUpload()
                                    }}
                                >
                                    Retry identity upload
                                </button>
                            </div>
                        </>
                    )}
                    {identityUpload.status === 'idle' && <span>No identity documents attached.</span>}
                </div>

                <div className={styles.sendActions}>
                    {clientId && (
                        <button
                            type="button"
                            className={styles.primaryButton}
                            onClick={() => navigate(`/military-hearing-loss/client/${clientId}`)}
                            disabled={uploading}
                        >
                            View client record
                        </button>
                    )}
                    <button type="button" className={styles.secondaryButton} onClick={resetAll} disabled={uploading}>
                        Add another client
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.pageLayout}>
        <form
            className={styles.form}
            onSubmit={handleSubmit}
            autoComplete="off"
            onFocus={(e) => {
                const target = e.target as HTMLElement
                const section = target.closest('section[id]') as HTMLElement | null
                if (!section) return
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        // Only scroll if the focused element itself is not already visible.
                        // Checking the target (not the section) avoids yanking back to the
                        // section top when inside a tall section like Service Records.
                        const rect = target.getBoundingClientRect()
                        const inView = rect.top >= 0 && rect.bottom <= window.innerHeight
                        if (!inView) {
                            section.scrollIntoView({ behavior: 'smooth', block: 'start' })
                        }
                    })
                })
            }}
        >
            <section id="mhl-section-personal" className={`${styles.section}${sectionComplete.personal ? ` ${styles.sectionComplete}` : ''}`}>
                <h3 className={sectionComplete.personal ? styles.sectionHeaderComplete : ''}>Personal Details{sectionComplete.personal && <SectionCheck />}</h3>
                <div className={styles.grid}>
                    <label className={styles.field}>
                        <span>Title<span className={styles.req}>*</span></span>
                        <select id="mhl-field-title" value={form.title} onChange={(e) => setField('title', e.target.value)}>
                            <option value="">Select…</option>
                            {TITLES.map((t) => (
                                <option key={t} value={t}>
                                    {t}
                                </option>
                            ))}
                        </select>
                    </label>
                    <div className={styles.gridTwo}>
                        <label className={styles.field}>
                            <span>
                                First Name<span className={styles.req}>*</span>
                            </span>
                            <input id="mhl-field-firstName" value={form.firstname} onChange={(e) => setField('firstname', toTitleCase(e.target.value))} placeholder="e.g. John" />
                        </label>
                        <label className={styles.field}>
                            <span>Middle Name(s)</span>
                            <input value={form.middleNames} onChange={(e) => setField('middleNames', toTitleCase(e.target.value))} />
                        </label>
                    </div>
                    <label className={styles.field}>
                        <span>
                            Surname<span className={styles.req}>*</span>
                        </span>
                        <input id="mhl-field-surname" value={form.lastname} onChange={(e) => setField('lastname', toTitleCase(e.target.value))} placeholder="e.g. Smith" />
                    </label>
                    <label className={styles.field}>
                        <span>
                            Date of Birth<span className={styles.req}>*</span>
                        </span>
                        <DateInput
                            id="mhl-field-dob"
                            value={form.dob}
                            onChange={(v) => setField('dob', v)}
                            minAge={16}
                            onComplete={() => (document.getElementById('mhl-field-ni') as HTMLInputElement)?.focus()}
                            onError={setDobError}
                        />
                    </label>
                    <label className={styles.field}>
                        <span>National Insurance<span className={styles.req}>*</span></span>
                        <div className={styles.fieldWithInlineError}>
                            <input
                                id="mhl-field-ni"
                                value={form.nationalInsurance}
                                onChange={(e) => handleNiChange(e.target.value)}
                                placeholder="e.g. AB123456C"
                            />
                            {niError && <span className={styles.fieldError}>{niError}</span>}
                        </div>
                    </label>
                    <IdentityDocumentsInput
                        poid={poidFile}
                        utilityBill={utilityBillFile}
                        onPoidChange={setPoidFile}
                        onUtilityBillChange={setUtilityBillFile}
                        required
                        disabled={isSubmitting}
                    />
                </div>
            </section>

            <section id="mhl-section-address" className={`${styles.section}${sectionComplete.address ? ` ${styles.sectionComplete}` : ''}`}>
                <h3 className={sectionComplete.address ? styles.sectionHeaderComplete : ''}>Address{sectionComplete.address && <SectionCheck />}</h3>
                <div className={styles.grid}>
                    <div className={styles.gridSpan}>
                        <SegmentedToggle
                            label="Where does the client live?"
                            required
                            value={addressRegion}
                            options={ADDRESS_REGION_OPTIONS}
                            onChange={selectAddressRegion}
                        />
                    </div>
                    {addressRegion === 'international' && (
                        <p className={`${styles.gridSpan} ${styles.infoNote}`}>
                            Search the full address, including the house number and street. Unlike the UK search, it
                            won't list the houses for a postcode.
                        </p>
                    )}
                    {/* div, not label: a label forwards clicks on a result back to the search input, which re-opens the list */}
                    <div className={styles.field}>
                        <span>Search address</span>
                        {addressRegion === 'uk' ? (
                            <InputFieldAddressLookup
                                key={addressKey}
                                fieldName="address"
                                saveValue={handleReusableSave}
                                enhanced={true}
                                placeholder="Enter postcode..."
                            />
                        ) : (
                            <InputFieldGoogleAddressLookup
                                key={addressKey}
                                fieldName="address"
                                onSelect={selectGoogleAddress}
                                enhanced={true}
                            />
                        )}
                        {!manualAddress && (
                            <button
                                type="button"
                                className={styles.manualAddressButton}
                                onClick={() => setManualAddress(true)}
                            >
                                Enter address manually
                            </button>
                        )}
                    </div>
                    {(selectedAddressRaw || manualAddress) && (
                        <>
                            <label className={styles.field}>
                                <span>Address line 1<span className={styles.req}>*</span></span>
                                <div className={styles.fieldWithInlineError}>
                                    <input id="mhl-field-address1" value={form.address1} onChange={(e) => handleAddress1Change(e.target.value)} />
                                    {buildingNumberMissing && <span className={styles.fieldError}>Please add the house or building number</span>}
                                </div>
                            </label>
                            <label className={styles.field}>
                                <span>Address line 2</span>
                                <input value={form.address2} onChange={(e) => setField('address2', e.target.value)} />
                            </label>
                            <label className={styles.field}>
                                <span>Address line 3 (City)<span className={styles.req}>*</span></span>
                                <input value={form.address3} onChange={(e) => setField('address3', e.target.value)} />
                            </label>
                            <label className={styles.field}>
                                <span>Address line 4</span>
                                <input value={form.address4} onChange={(e) => setField('address4', e.target.value)} />
                            </label>
                            {addressRegion === 'international' && (
                                <label className={styles.field}>
                                    <span>Country<span className={styles.req}>*</span></span>
                                    <select id="mhl-field-country" value={form.country} onChange={(e) => handleCountryChange(e.target.value)}>
                                        <option value="">Select country</option>
                                        {countryOptions.map((c) => (
                                            <option key={c.iso2} value={c.iso2}>
                                                {c.name}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            )}
                            <label className={styles.field}>
                                <span>
                                    {form.country === 'GB' ? 'Postcode' : 'Postcode / ZIP code'}
                                    {form.country === 'GB' && <span className={styles.req}>*</span>}
                                </span>
                                <div className={styles.fieldWithInlineError}>
                                    <input id="mhl-field-postcode" value={form.postcode} onChange={(e) => handlePostcodeChange(e.target.value)} />
                                    {postcodeError && <span className={styles.fieldError}>{postcodeError}</span>}
                                </div>
                            </label>
                        </>
                    )}
                </div>
            </section>

            <section id="mhl-section-contact" className={`${styles.section}${sectionComplete.contact ? ` ${styles.sectionComplete}` : ''}`}>
                <h3 className={sectionComplete.contact ? styles.sectionHeaderComplete : ''}>Contact{sectionComplete.contact && <SectionCheck />}</h3>
                <div className={styles.grid}>
                    <label className={styles.field}>
                        <span>Home Telephone</span>
                        <div className={styles.fieldWithInlineError}>
                            <input
                                type="tel"
                                value={form.homeTelephone}
                                inputMode="tel"
                                placeholder="e.g. 01234 567890"
                                onChange={(e) => handleHomePhoneChange(e.target.value)}
                            />
                            {homePhoneError && <span className={styles.fieldError}>{homePhoneError}</span>}
                        </div>
                    </label>
                    {/* div, not label: a label would forward clicks on its text to the dial code button */}
                    <div className={styles.field}>
                        <span>Mobile Number<span className={styles.req}>*</span></span>
                        <div className={styles.fieldWithInlineError}>
                            <div className={styles.phoneRow}>
                                <DialCodeSelect
                                    value={form.telephoneCountry}
                                    onChange={handleMobileCountryChange}
                                    error={!!mobileError}
                                />
                                <input
                                    id="mhl-field-mobile"
                                    type="tel"
                                    aria-label="Mobile Number"
                                    value={form.telephone}
                                    onChange={(e) => handleMobileChange(e.target.value)}
                                    // The dial code is picked separately, so examples leave off the leading 0
                                    placeholder={form.telephoneCountry === 'GB' ? 'e.g. 7700 900123' : 'Number without the leading 0'}
                                />
                            </div>
                            {mobileError && <span className={styles.fieldError}>{mobileError}</span>}
                        </div>
                    </div>
                    <label className={styles.field}>
                        <span>Email Address<span className={styles.req}>*</span></span>
                        <div className={styles.fieldWithInlineError}>
                            <input
                                id="mhl-field-email"
                                type="email"
                                value={form.email}
                                onChange={(e) => handleEmailChange(e.target.value)}
                            />
                            {emailError && <span className={styles.fieldError}>{emailError}</span>}
                        </div>
                    </label>
                </div>
            </section>

            <section id="mhl-section-services" className={`${styles.section}${sectionComplete.services ? ` ${styles.sectionComplete}` : ''}`}>
                <h3 className={sectionComplete.services ? styles.sectionHeaderComplete : ''}>Service Records{sectionComplete.services && <SectionCheck />}</h3>
                {form.services.map((service, index) => (
                    <div key={service.id} className={styles.serviceBlock}>
                        <div className={styles.sectionHeader}>
                            <h4>Service {index + 1}</h4>
                            {form.services.length > 1 && (
                                <button
                                    type="button"
                                    className={styles.removeButton}
                                    onClick={() => removeService(index)}
                                >
                                    Remove
                                </button>
                            )}
                        </div>
                        <div className={styles.grid}>
                            <label className={styles.field}>
                                <span>Service Number<span className={styles.req}>*</span></span>
                                <div className={styles.fieldWithInlineError}>
                                    <input
                                        id={`mhl-field-service-${index}-number`}
                                        value={service.serviceNumber}
                                        onChange={(e) => setServiceField(index, 'serviceNumber', e.target.value.toUpperCase())}
                                        placeholder="e.g. 30012345"
                                    />
                                    {t(service.serviceNumber) && !SERVICE_NUMBER_REGEX.test(service.serviceNumber.trim()) && (
                                        <span className={styles.fieldError}>Please enter a valid service number</span>
                                    )}
                                </div>
                            </label>
                            <label className={styles.field}>
                                <span>Branch<span className={styles.req}>*</span></span>
                                <select
                                    id={`mhl-field-service-${index}-branch`}
                                    value={service.branch}
                                    onChange={(e) =>
                                        setServiceField(index, 'branch', e.target.value as typeof service.branch)
                                    }
                                >
                                    <option value="">Select…</option>
                                    {SERVICE_BRANCHES.map((b) => (
                                        <option key={b} value={b}>
                                            {b}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className={styles.field}>
                                <span>Enlistment Date<span className={styles.req}>*</span></span>
                                <DateInput
                                    id={`mhl-field-service-${index}-enlistment`}
                                    value={service.enlistmentDate}
                                    onChange={(v) => setServiceField(index, 'enlistmentDate', v)}
                                    onError={(err) => setServiceDateErrors((prev) => ({ ...prev, [`${service.id}-enlistment`]: !!err }))}
                                />
                            </label>
                            <div id={`mhl-field-service-${index}-activeService`}>
                                <ToggleInput
                                    spacious
                                    label="Still serving in this role?"
                                    value={service.activeService}
                                    onChange={(v) => {
                                        setServiceField(index, 'activeService', v)
                                        // Discharge date no longer applies once marked as still
                                        // serving — clear any value and its error.
                                        if (v) {
                                            setServiceField(index, 'dischargeDate', '')
                                            setServiceDateErrors((prev) => ({ ...prev, [`${service.id}-discharge`]: false }))
                                        }
                                    }}
                                    required
                                />
                            </div>
                            {!service.activeService && (
                                <label className={styles.field}>
                                    <span>Discharge Date<span className={styles.req}>*</span></span>
                                    <DateInput
                                        id={`mhl-field-service-${index}-discharge`}
                                        value={service.dischargeDate}
                                        onChange={(v) => setServiceField(index, 'dischargeDate', v)}
                                        minDate={service.enlistmentDate || undefined}
                                        onError={(err) => setServiceDateErrors((prev) => ({ ...prev, [`${service.id}-discharge`]: !!err }))}
                                    />
                                </label>
                            )}
                        </div>
                    </div>
                ))}
                {form.services.length < 3 && (
                    <button type="button" className={`${styles.secondaryButton} ${styles.addServiceButton}`} onClick={addService}>
                        + Add service
                    </button>
                )}
            </section>

            <section id="mhl-section-medical" className={`${styles.section}${sectionComplete.medical ? ` ${styles.sectionComplete}` : ''}`}>
                <h3 className={sectionComplete.medical ? styles.sectionHeaderComplete : ''}>Medical Indicators{sectionComplete.medical && <SectionCheck />}</h3>
                <div className={styles.grid}>
                    <div id="mhl-field-audiology" className={styles.gridSpan}>
                        <ToggleInput
                            spacious
                            label="Audiology result"
                            value={form.audiology || null}
                            options={AUDIOLOGY_OPTIONS}
                            onChange={(v) => setField('audiology', v)}
                            required
                            labelRight={
                                form.audiology === '>=4dB' ? (
                                    <span className={`${styles.audiologyPill} ${styles.pillPass}`}>Above quantum</span>
                                ) : form.audiology === '<4dB' ? (
                                    <span className={`${styles.audiologyPill} ${styles.pillReject}`}>Below quantum</span>
                                ) : form.audiology === 'tbc' ? (
                                    <span className={`${styles.audiologyPill} ${styles.pillReview}`}>Not calculated yet</span>
                                ) : null
                            }
                        />
                    </div>
                    <AudiologyFileInput file={audiogramFile} onChange={setAudiogramFile} required disabled={isSubmitting} />
                    <div id="mhl-field-tinnitus">
                        <ToggleInput
                            spacious
                            label="Tinnitus (ringing/buzzing in ears)"
                            value={form.hearingLossOrTinnitus}
                            onChange={(v) => setField('hearingLossOrTinnitus', v)}
                            required
                        />
                    </div>
                    <div id="mhl-field-hyperacusis">
                        <ToggleInput
                            spacious
                            label="Hyperacusis (sensitivity/pain from loud noises)"
                            value={form.hyperacusis}
                            onChange={(v) => setField('hyperacusis', v)}
                            required
                        />
                    </div>
                    <div id="mhl-field-altNoise">
                        <ToggleInput
                            spacious
                            label="Alternative Noise Exposure"
                            value={form.alternativeNoiseExposure || null}
                            options={[
                                { value: 'none', label: 'None' },
                                { value: 'some', label: 'Some' },
                            ]}
                            onChange={(v) => setField('alternativeNoiseExposure', v)}
                            required
                        />
                    </div>
                    <div id="mhl-field-altCivilian">
                        <ToggleInput
                            spacious
                            label="Alternate Civilian Exposure"
                            value={form.alternateCivilianExposure}
                            onChange={(v) => setField('alternateCivilianExposure', v)}
                            required
                        />
                    </div>
                </div>
            </section>

            <section id="mhl-section-other" className={`${styles.section}${sectionComplete.other ? ` ${styles.sectionComplete}` : ''}`}>
                <h3 className={sectionComplete.other ? styles.sectionHeaderComplete : ''}>Other{sectionComplete.other && <SectionCheck />}</h3>
                <div className={styles.grid}>
                    <div id="mhl-field-afcs">
                        <ToggleInput
                            spacious
                            label="Successful AFCS / War Pension Claim for hearing loss"
                            value={form.successfulAfcsWarPensionClaim}
                            onChange={(v) => {
                                setField('successfulAfcsWarPensionClaim', v)
                                // Award amount only applies when the claim was successful —
                                // clear any previously entered value if the answer changes away from yes.
                                if (!v) setField('afcsAwardAmount', null)
                            }}
                            required
                        />
                    </div>
                    {form.successfulAfcsWarPensionClaim === true && (
                        <label className={styles.field}>
                            <span>Award amount (£)<span className={styles.req}>*</span></span>
                            <div className={styles.fieldWithInlineError}>
                                <input
                                    type="number"
                                    inputMode="decimal"
                                    min="0.01"
                                    step="0.01"
                                    value={form.afcsAwardAmount ?? ''}
                                    onChange={(e) => {
                                        const raw = e.target.value
                                        setField('afcsAwardAmount', raw === '' ? null : Math.abs(parseFloat(raw)))
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === '-') e.preventDefault()
                                    }}
                                />
                                {form.afcsAwardAmount !== null && form.afcsAwardAmount <= 0 && (
                                    <span className={styles.fieldError}>Award amount must be greater than £0</span>
                                )}
                            </div>
                        </label>
                    )}
                    <div id="mhl-field-nfci">
                        <ToggleInput
                            spacious
                            label="NFCI Claim"
                            value={form.nfciClaim}
                            onChange={(v) => setField('nfciClaim', v)}
                            required
                        />
                    </div>
                    <div id="mhl-field-specialForces">
                        <ToggleInput
                            spacious
                            label="Special Forces"
                            value={form.specialForces}
                            onChange={(v) => setField('specialForces', v)}
                            required
                        />
                    </div>
                </div>
            </section>

            <section id="mhl-section-confirmation" className={`${styles.section}${sectionComplete.confirmation ? ` ${styles.sectionComplete}` : ''}`}>
                <h3 className={sectionComplete.confirmation ? styles.sectionHeaderComplete : ''}>Confirmation{sectionComplete.confirmation && <SectionCheck />}</h3>
                <p className={styles.confirmationIntro}>Before submitting, please confirm the following with the client.</p>
                <ul className={styles.confirmationList}>
                    <li>The client has been told their claim will be enrolled in the Matrix journey.</li>
                    <li>The client understands their claim will be managed and settled through the Matrix redress scheme rather than by litigation.</li>
                    <li>The client has agreed to be enrolled and for their details to be shared for that purpose.</li>
                </ul>
                <div id="mhl-field-matrixOptIn">
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            checked={form.matrixOptInConfirmed}
                            disabled={isSubmitting}
                            onChange={(e) => setField('matrixOptInConfirmed', e.target.checked)}
                        />
                        <span>I confirm the client has opted in to enrol in the Matrix journey<span className={styles.req}>*</span></span>
                    </label>
                </div>
            </section>

            {submitError && <div className={styles.errorNote}>{submitError}</div>}

            {onlyConfirmationOutstanding && (
                <div className={styles.submitHint}>Tick the Matrix opt-in confirmation above to enable Submit.</div>
            )}

            <div className={styles.actionRow}>
                <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() => { resetAll(); document.getElementById('main-view')?.scrollTo({ top: 0, behavior: 'smooth' }) }}
                >
                    Reset
                </button>
                <button type="submit" className={styles.primaryButton} disabled={!isValid || !emailData8Valid || !mobileData8Valid || buildingNumberMissing || isSubmitting}>
                    {isSubmitting ? 'Submitting…' : 'Submit'}
                </button>
            </div>
        </form>
            <FormProgressPanel
                form={form}
                emailData8Valid={emailData8Valid}
                mobileData8Valid={mobileData8Valid}
                dobError={!!dobError}
                serviceDateErrors={serviceDateErrors}
                hasAudiogram={audiogramFile !== null}
                hasPoid={poidFile !== null}
                hasUtilityBill={utilityBillFile !== null}
            />
            {process.env.NODE_ENV === 'development' && authUser?.['custom:organisation'] === 'Sentinel' && (
                <PayloadDebugPanel form={form} agentLabel={agentLabel} />
            )}
        </div>
    )
}
